import { Component, type ErrorInfo, type ReactNode } from "react";
import { clearSession } from "@/lib/storage";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time exceptions anywhere under the Router so a single page
 * bug shows a recovery card instead of unmounting the whole app (blank page).
 * Static bilingual copy on purpose: LanguageProvider may itself be the thing
 * that crashed, so no hooks/providers are used here.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept on purpose even in prod builds? No: vite drops console.* in prod,
    // so this only helps in dev/uat. Wire Sentry here when it exists.
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  private reload = () => {
    window.location.reload();
  };

  private resetAndLogin = () => {
    clearSession();
    window.location.href = "/login";
  };

  render() {
    if (!this.state.error) return this.props.children;

    const isPt = (navigator.language || "").toLowerCase().startsWith("pt");

    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-6">
        <div className="w-full max-w-md rounded-xl border bg-card p-6 shadow-sm text-card-foreground">
          <h1 className="text-lg font-semibold mb-1">
            {isPt ? "Algo deu errado nesta tela" : "Something went wrong on this screen"}
          </h1>
          <p className="text-sm text-muted-foreground mb-4">
            {isPt
              ? "O erro foi registrado. Recarregue a página ou saia e entre novamente."
              : "The error was logged. Reload the page or sign out and back in."}
          </p>
          <pre className="text-xs bg-muted rounded p-3 overflow-auto max-h-32 mb-4 whitespace-pre-wrap break-words">
            {this.state.error.message}
          </pre>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={this.resetAndLogin}
              className="inline-flex items-center rounded-md border px-3 py-2 text-sm hover:bg-accent"
            >
              {isPt ? "Sair e entrar de novo" : "Sign out and back in"}
            </button>
            <button
              type="button"
              onClick={this.reload}
              className="inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            >
              {isPt ? "Recarregar" : "Reload"}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
